import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

export interface ClientBranding {
  displayName?: string | null;
  hasLogo?: boolean;
  logoUrl?: string | null;
  primaryColor?: string | null;
  logoMeta?: { width: number; height: number; shape?: string } | null;
}

export interface ClientUser {
  id: number;
  email: string;
  name: string;
  role: string;
  clientId: number;
  companyName: string | null;
  clientCode: string | null;
  whiteLabel: string | null;
  branding?: ClientBranding | null;
  depositReference?: string | null;
  mustChangePassword?: boolean;
  onboardingPhase?: 'operational' | 'onboarding' | 'pending_admin_review' | 'rejected';
  canAccessDisbursements?: boolean;
  revenueShareEnabled?: boolean;
  passthroughEnabled?: boolean;
  onboardingStatus?: string | null;
  kybStatus?: string | null;
  clientStatus?: string | null;
}

interface ClientAuthContextType {
  user: ClientUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  mustChangePassword: boolean;
  login: (email: string, password: string) => Promise<{
    success: boolean;
    error?: string;
    mustChangePassword?: boolean;
    onboardingPhase?: ClientUser['onboardingPhase'];
    canAccessDisbursements?: boolean;
  }>;
  logout: () => void;
  getToken: () => string | null;
  refreshUser: () => Promise<void>;
}

const ClientAuthContext = createContext<ClientAuthContextType | undefined>(undefined);

const API_BASE = '/api/v1/client-portal';
const TOKEN_KEY = 'client_portal_token';
const USER_KEY = 'client_portal_user';

interface ClientAuthProviderProps {
  children: ReactNode;
}

function mapUserFromApi(data: Record<string, unknown>): ClientUser {
  return {
    id: data.id as number,
    email: data.email as string,
    name: data.name as string,
    role: data.role as string,
    clientId: data.clientId as number,
    companyName: (data.companyName as string) || null,
    clientCode: (data.clientCode as string) || null,
    whiteLabel: (data.whiteLabel as string) || null,
    branding: (data.branding as ClientBranding) || null,
    depositReference: (data.depositReference as string) || null,
    mustChangePassword: !!(data.mustChangePassword),
    onboardingPhase: (data.onboardingPhase as ClientUser['onboardingPhase']) || 'onboarding',
    canAccessDisbursements: !!(data.canAccessDisbursements),
    revenueShareEnabled: !!(data.revenueShareEnabled),
    passthroughEnabled: !!(data.passthroughEnabled),
    onboardingStatus: (data.onboardingStatus as string) || null,
    kybStatus: (data.kybStatus as string) || null,
    clientStatus: (data.clientStatus as string) || null,
  };
}

export const ClientAuthProvider: React.FC<ClientAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<ClientUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const clearSession = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  const getToken = useCallback((): string | null => {
    return sessionStorage.getItem(TOKEN_KEY);
  }, []);

  const verifyToken = useCallback(async (): Promise<boolean> => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) {
      clearSession();
      return false;
    }

    try {
      const res = await fetch(`${API_BASE}/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        clearSession();
        return false;
      }

      const data = await res.json();
      if (data.success && data.data) {
        const userData = mapUserFromApi(data.data);
        setUser(userData);
        sessionStorage.setItem(USER_KEY, JSON.stringify(userData));
        return true;
      }

      clearSession();
      return false;
    } catch {
      clearSession();
      return false;
    }
  }, [clearSession]);

  const refreshUser = useCallback(async () => {
    await verifyToken();
  }, [verifyToken]);

  useEffect(() => {
    const init = async () => {
      const token = sessionStorage.getItem(TOKEN_KEY);
      const userData = sessionStorage.getItem(USER_KEY);

      if (token && userData) {
        try {
          setUser(JSON.parse(userData));
        } catch {
          clearSession();
        }
        await verifyToken();
      }
      setIsLoading(false);
    };
    init();
  }, [verifyToken, clearSession]);

  const login = async (email: string, password: string): Promise<{
    success: boolean;
    error?: string;
    mustChangePassword?: boolean;
    onboardingPhase?: ClientUser['onboardingPhase'];
    canAccessDisbursements?: boolean;
  }> => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        return { success: false, error: data.error || 'Login failed' };
      }

      sessionStorage.setItem(TOKEN_KEY, data.data.token);
      const userData = mapUserFromApi(data.data.user);
      sessionStorage.setItem(USER_KEY, JSON.stringify(userData));
      setUser(userData);

      return {
        success: true,
        mustChangePassword: userData.mustChangePassword,
        onboardingPhase: userData.onboardingPhase,
        canAccessDisbursements: userData.canAccessDisbursements,
      };
    } catch {
      return { success: false, error: 'Network error. Please try again.' };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    clearSession();
  };

  const mustChangePassword = !!user?.mustChangePassword;

  return (
    <ClientAuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        mustChangePassword,
        login,
        logout,
        getToken,
        refreshUser,
      }}
    >
      {children}
    </ClientAuthContext.Provider>
  );
};

export const useClientAuth = (): ClientAuthContextType => {
  const context = useContext(ClientAuthContext);
  if (context === undefined) {
    throw new Error('useClientAuth must be used within a ClientAuthProvider');
  }
  return context;
};
